import React from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link, Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { hasUnsavedEntry } from './utils/fillingMeasurements';

const isBlank = (value) => value === undefined || value === null || value === '';

// Asked when a row with volume measurements becomes full or empty: the calculation
// ignores them (capacity or zero). Clearing is the default; keeping stays possible
// and the row then shows in the admin "Items to check" table. Keeping is a discreet
// link in the content, away from the two buttons, so it cannot be tapped by mistake.
export default function FillingMeasurementsDialog({
  request, loading, onCancel, onKeep, onClear,
}) {
  const { t } = useTranslation();
  if (!request) return null;
  const format = (value, unit) => (isBlank(value)
    ? t('operation.filling_measurements.empty_value')
    : `${String(value)} ${unit || ''}`.trim());
  return (
    <Dialog open onClose={onCancel} fullWidth maxWidth="xs" aria-labelledby="filling-measurements-title">
      <DialogTitle id="filling-measurements-title">{t('operation.filling_measurements.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>
          {t(`operation.filling_measurements.description_${request.filling}`)}
        </DialogContentText>
        {request.measurements.map((measurement) => {
          const {
            field, unit, saved, entered,
          } = measurement;
          // A saved value and a different unsaved entry are both shown: either can be the reason.
          if (hasUnsavedEntry(measurement)) {
            return (
              <Typography key={field} component="div" variant="body2" sx={{ mb: 0.5 }}>
                <b>{field}</b>
                <div>{t('operation.filling_measurements.saved_value', { value: format(saved, unit) })}</div>
                <div>{t('operation.filling_measurements.entered_value', { value: format(entered, unit) })}</div>
              </Typography>
            );
          }
          return (
            <Typography key={field} variant="body2" sx={{ fontWeight: 600 }}>
              {`${field} : ${format(isBlank(entered) ? saved : entered, unit)}`}
            </Typography>
          );
        })}
        <Link
          component="button"
          type="button"
          variant="body2"
          color="text.secondary"
          onClick={onKeep}
          disabled={loading}
          sx={{ display: 'block', mt: 3 }}
        >
          {t('operation.filling_measurements.keep')}
        </Link>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={loading}>{t('cancel')}</Button>
        <LoadingButton variant="contained" onClick={onClear} loading={loading}>
          {t('operation.filling_measurements.clear')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
